import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calculator, Loader2, RefreshCw, Save, Info, CheckCircle2, AlertTriangle, HelpCircle, ChevronUp, ChevronDown, X, Zap, Sparkles, Check, ArrowUp, ArrowDown, FileDown, Layers, ClipboardList, Users2, Wrench, Briefcase, ShieldCheck, Trash2, Plus, GripVertical, Copy, MoveRight } from "lucide-react";
import PeerWinsCalibrationCard from "@/components/PeerWinsCalibrationCard";
import RecommendedPricingCard from "@/components/RecommendedPricingCard";
import RateCardAutoMatchCard from "@/components/RateCardAutoMatchCard";
import EstimateVariantTabs from "@/components/EstimateVariantTabs";
import PageTour from "@/components/PageTour";
import { Calculator as CalcIcon, Sparkles as Sparkles2, FileText, ListChecks } from "lucide-react";

export default function Estimate() {
  const { noticeId } = useParams();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);   // compliance sweep runs in the background after generate
  const [saving, setSaving] = useState(false);
  const [opp, setOpp] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewIdx, setReviewIdx] = useState(0);
  const rowRefs = useRef({});
  const [suggestions, setSuggestions] = useState({});
  const [rec, setRec] = useState(null);
  const [rescoreDismissed, setRescoreDismissed] = useState(false);
  const [wrapRates, setWrapRates] = useState([]);
  const [wrapRateId, setWrapRateId] = useState(null);
  const [boeOpen, setBoeOpen] = useState(false);
  const [boe, setBoe] = useState(null);
  const [boePicker, setBoePicker] = useState(null); // {ci, ii, description, candidates, loading}
  const isServices = est?.mode === "services";
  const mode = isServices ? "services" : "materials";

  const [ingesting, setIngesting] = useState(false);

  useEffect(() => {
    api.get(`/opportunities/${noticeId}`).then(async (r) => {
      setOpp(r.data);
      // Auto-fire attachment ingestion the FIRST time an operator opens the
      // Estimator on an opp that has SAM.gov attachments but no takeoff yet.
      // Silent — no toast on completion (unless the operator explicitly kicks
      // it off from the Opportunity Detail page). Prevents the "we bid $425k
      // on a $15-25k job" class of mistake.
      const opp = r.data || {};
      if ((opp.resource_links?.length || 0) > 0 && !opp.attachment_takeoff && !ingesting) {
        setIngesting(true);
        try {
          const t = await api.post(`/opportunities/${noticeId}/ingest-attachments`, {}, { timeout: 240000 });
          // Refresh the opp so the takeoff data lands on state
          const fresh = await api.get(`/opportunities/${noticeId}`);
          setOpp(fresh.data);
          const mag = t.data?.attachment_takeoff?.magnitude;
          if (mag?.low) {
            toast.message(`Attachment takeoff done · Magnitude $${Number(mag.low).toLocaleString()}${mag.high ? `–$${Number(mag.high).toLocaleString()}` : ""}`);
          }
        } catch (e) {
          console.warn("Auto-ingest skipped:", e?.response?.data?.detail || e?.message);
        } finally {
          setIngesting(false);
        }
      }
    }).catch(() => {});
    api.get(`/ai/estimate/${noticeId}`).then(r => setEst(r.data)).catch(() => {});
    api.get(`/ai/estimate/${noticeId}/recommended-total`).then(r => setRec(r.data)).catch(() => {});
    api.get(`/wrap-rates`).then((r) => {
      setWrapRates(r.data || []);
      const def = (r.data || []).find((w) => w.is_default) || (r.data || [])[0];
      if (def) setWrapRateId(def.rate_id);
    }).catch(() => {});
  }, [noticeId]);

  // After the base estimate lands, run the compliance sweep in the background.
  // This used to be inline with generate but pushed the total request past
  // Cloudflare's ~100s ingress timeout on production. Best-effort: if the
  // sweep fails or times out, the estimate is still usable — operator just
  // won't see the auto-appended Compliance & Contractual category.
  const runComplianceSweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post(`/ai/estimate/${noticeId}/compliance-sweep`, {}, { timeout: 180000 });
      setEst(r.data);
      const n = r.data?.compliance?.priced_items_added || 0;
      const c = r.data?.compliance?.checklist?.length || 0;
      if (n > 0 || c > 0) {
        toast.success(`Compliance sweep: ${n} priced item${n === 1 ? "" : "s"} + ${c} checklist item${c === 1 ? "" : "s"}`);
      }
    } catch (e) {
      // Silent failure — sweep is optional, don't block the operator
      console.warn("Compliance sweep skipped:", e?.response?.data?.detail || e?.message);
    } finally {
      setSweeping(false);
    }
  };

  // Show refit toast once per estimate load — sanity gate telemetry.
  useEffect(() => {
    if (!est?.refit?.fired || !est?.refit?.accepted) return;
    const from = est.refit.from_total;
    const to = est.refit.to_total;
    const anchorLabel = est.refit.anchor_type === "magnitude"
      ? "magnitude"
      : est.refit.anchor_type === "peer" ? "peer wins" : "market band";
    toast.message(`Auto-refit: $${Number(from).toLocaleString(undefined, {maximumFractionDigits: 0})} → $${Number(to).toLocaleString(undefined, {maximumFractionDigits: 0})}`, {
      duration: 8000,
      description: `Sanity gate refit the estimate to fit the ${anchorLabel} anchor. See assumptions for detail.`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [est?.refit?.fired, est?.refit?.accepted, est?.refit?.from_total, est?.refit?.to_total]);

  const runAgent = async (force = false) => {
    setLoading(true);
    try {
      // Iter-138.4: estimator is now an async background job. POST
      // returns instantly with {job_id, status}; then we poll
      // /job/status every 3s. If the server returns a cached estimate
      // synchronously (status=="done" on the first response), we
      // short-circuit and use it directly.
      const r = await api.post(`/ai/estimate/${noticeId}`, { force, async_mode: true }, { timeout: 30000 });
      if (r.data?.status === "done" && r.data?.estimate) {
        setEst(r.data.estimate);
        toast.success(force ? "Estimate regenerated (cached)" : "Estimate ready");
        runComplianceSweep();
        return;
      }
      // Poll for up to 8 minutes (160 * 3s) — well beyond any realistic
      // Claude latency. Frontend badge shows the live `progress` string.
      let _tries = 0;
      const _poll = setInterval(async () => {
        _tries += 1;
        try {
          const s = await api.get(`/ai/estimate/${noticeId}/job/status`, { timeout: 15000 });
          const _st = s.data?.status;
          if (_st === "done" && s.data?.estimate) {
            clearInterval(_poll);
            setEst(s.data.estimate);
            toast.success(force ? "Estimate regenerated" : "Estimate ready");
            runComplianceSweep();
            setLoading(false);
          } else if (_st === "error") {
            clearInterval(_poll);
            const _err = s.data?.error || "Estimating Agent failed";
            if (/timeout|timed out|warmed up/i.test(String(_err))) {
              toast.error(_err, { duration: 8000 });
            } else {
              toast.error(_err);
            }
            setLoading(false);
          } else if (_tries >= 160) {
            clearInterval(_poll);
            toast.error("Estimator is taking longer than usual — please try again.", { duration: 8000 });
            setLoading(false);
          }
          // else: still queued/running — spinner stays on
        } catch (_) {
          // Transient error (network blip during a long generation) —
          // don't kill the poll, just try again next tick.
        }
      }, 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to start estimator");
      setLoading(false);
    }
  };

  const runServicesAgent = async () => {
    if (!wrapRateId) {
      toast.error("Add a wrap rate first (Company > Wrap Rates) — services mode needs it to compute cost-to-gov.");
      return;
    }
    if (!window.confirm("Switch to SERVICES mode (labor cat × hours × wrap)? Your current line items will be replaced with a fresh labor buildup. Costs 1 credit.")) return;
    setLoading(true);
    try {
      const r = await api.post(`/ai/estimate/${noticeId}/services-mode`, { force: true, wrap_rate_id: wrapRateId }, { timeout: 180000 });
      setEst(r.data);
      const snaps = (r.data.rate_card_snaps || []).length;
      if (snaps > 0) {
        toast.success(`Services-mode estimate generated · ${snaps} line${snaps === 1 ? "" : "s"} auto-snapped to your rate card`);
      } else {
        toast.success("Services-mode estimate generated");
      }
      runComplianceSweep();
    } catch (e) { toast.error(e?.response?.data?.detail || "Services-mode failed"); }
    finally { setLoading(false); }
  };

  const openBoe = async () => {
    setBoeOpen(true);
    if (!boe) {
      try { const r = await api.get(`/ai/estimate/${noticeId}/basis-of-estimates`); setBoe(r.data); }
      catch { toast.error("Could not load Basis of Estimates"); }
    }
  };

  // Find (category_index, item_index) in the current estimate for a line surfaced by BOE.
  const locateLine = (line) => {
    for (let ci = 0; ci < (est?.categories || []).length; ci++) {
      const items = est.categories[ci].items || [];
      for (let ii = 0; ii < items.length; ii++) {
        if (items[ii].code === line.code && items[ii].description === line.description) return { ci, ii };
      }
    }
    return null;
  };

  const openPicker = async (line) => {
    const loc = locateLine(line);
    if (!loc) { toast.error("Could not locate this line in the estimate"); return; }
    setBoePicker({ ...loc, description: line.description, candidates: [], loading: true });
    try {
      const r = await api.get(`/ai/estimate/${noticeId}/rfp-paragraphs`, { params: { q: line.description } });
      setBoePicker((p) => p && ({ ...p, candidates: r.data.paragraphs || [], loading: false }));
    } catch (e) {
      setBoePicker((p) => p && ({ ...p, loading: false }));
      toast.error("Could not load RFP paragraphs");
    }
  };

  const attachRef = async (excerpt) => {
    if (!boePicker) return;
    const { ci, ii } = boePicker;
    // Use the first 140 chars of the picked paragraph as the source_ref citation.
    const source_ref = (excerpt || "").trim().slice(0, 140);
    try {
      await api.post(`/ai/estimate/${noticeId}/set-source-ref`, {
        category_index: ci, item_index: ii, source_ref,
      });
      // Optimistically update local state.
      setEst((prev) => ({
        ...prev,
        categories: prev.categories.map((c, i) => i !== ci ? c : ({
          ...c,
          items: c.items.map((it, j) => j !== ii ? it : ({ ...it, source_ref })),
        })),
      }));
      // Refresh the BOE coverage view.
      const r = await api.get(`/ai/estimate/${noticeId}/basis-of-estimates`);
      setBoe(r.data);
      setBoePicker(null);
      toast.success("Source reference attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save reference");
    }
  };

  const [bulkFilling, setBulkFilling] = useState(false);
  const bulkFillBoe = async () => {
    setBulkFilling(true);
    try {
      const r = await api.post(`/ai/estimate/${noticeId}/bulk-fill-boe`, { threshold: 0.4, only_missing: true });
      if (r.data.no_paragraphs) {
        toast.error("This RFP has no long-form description text to match against.");
      } else if (r.data.attached === 0) {
        toast.info("Nothing attached — no missing-ref lines scored above 40% overlap. Try lowering the bar by attaching individually.");
      } else {
        toast.success(`Attached ${r.data.attached} references · ${r.data.skipped} skipped (weak match)`);
      }
      setEst(r.data.estimate);
      // Refresh BOE panel.
      const b = await api.get(`/ai/estimate/${noticeId}/basis-of-estimates`);
      setBoe(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk fill failed");
    } finally { setBulkFilling(false); }
  };

  const downloadPdf = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      // Blob might be an error JSON — sniff first 5 bytes
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      // The response is a Blob when responseType='blob'; unwrap it to read the error.
      let msg = "Download failed";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text())?.detail || msg; } catch {}
      } else if (data?.detail) { msg = data.detail; }
      toast.error(msg);
    }
  };

  // ------------------------------------------------------------------
  // Undo / redo — every destructive change (row delete, category delete,
  // bulk actions) snapshots the prior estimate to this stack BEFORE it
  // mutates state. A sonner toast with an UNDO action button restores
  // the snapshot when tapped. Stack is capped at 20 entries so
  // long-running sessions don't leak memory.
  const [selected, setSelected] = useState(() => new Set());   // "ci-ii" row keys
  const [dragKey, setDragKey] = useState(null);                // "ci-ii" of the row being dragged

  const snapshotAndSet = (mutator, label = "Change") => {
    setEst(prev => {
      const before = prev;
      const after = mutator(prev);
      // Fire the UNDO toast asynchronously so we don't hold up the render
      setTimeout(() => {
        toast.message(label, {
          duration: 5000,
          action: {
            label: "UNDO",
            onClick: () => setEst(before),
          },
        });
      }, 0);
      return after;
    });
  };

  const setPrice = (ci, ii, val) => {
    const field = isServices ? "direct_rate" : "unit_price";
    setEst(prev => ({ ...prev, categories: prev.categories.map((c, i) => i !== ci ? c : ({
      ...c, items: c.items.map((it, j) => j !== ii ? it : ({ ...it, [field]: val === "" ? null : parseFloat(val) }))
    })) }));
  };

  const setQty = (ci, ii, val) => {
    const field = isServices ? "hours" : "qty";
    setEst(prev => ({ ...prev, categories: prev.categories.map((c, i) => i !== ci ? c : ({
      ...c, items: c.items.map((it, j) => j !== ii ? it : ({ ...it, [field]: val === "" ? null : parseFloat(val) }))
    })) }));
  };

  // Inline description editing — click the cell to unlock, blur/enter to lock.
  // Operators regularly need to rename an AI-generated line ("Roof demo" →
  // "Roof demo including gutter/parapet trim") without regenerating the whole
  // WBS. Also allows renaming compliance items so the proposal reads right.
  const setDescription = (ci, ii, val) => {
    setEst(prev => ({ ...prev, categories: prev.categories.map((c, i) => i !== ci ? c : ({
      ...c, items: c.items.map((it, j) => j !== ii ? it : ({ ...it, description: val }))
    })) }));
  };

  // Remove a single line item. If the category ends up empty we KEEP it — the
  // operator may want to add fresh items into that bucket.
  const removeItem = (ci, ii) => {
    const desc = est?.categories?.[ci]?.items?.[ii]?.description || "line";
    snapshotAndSet(
      prev => ({
        ...prev,
        categories: prev.categories.map((c, i) => i !== ci ? c : ({ ...c, items: c.items.filter((_, j) => j !== ii) })),
      }),
      `Deleted "${desc.slice(0, 50)}"`,
    );
    // Also clear from selection if it was checked
    setSelected(s => {
      const next = new Set(s);
      next.delete(`${ci}-${ii}`);
      return next;
    });
  };

  // Add a blank line item to a category. Uses a monotonic code so it doesn't
  // collide with existing ones. Services mode gets an hours/direct_rate shape;
  // materials mode gets qty/unit_price/unit.
  const addItem = (ci) => {
    setEst(prev => {
      const cat = prev.categories[ci];
      const existingCodes = new Set((cat.items || []).map(it => (it.code || "").trim()).filter(Boolean));
      let n = (cat.items?.length || 0) + 1;
      let code = `${String(ci + 1).padStart(2, "0")}.${String(n).padStart(2, "0")}`;
      while (existingCodes.has(code)) { n += 1; code = `${String(ci + 1).padStart(2, "0")}.${String(n).padStart(2, "0")}`; }
      const blank = isServices
        ? { code, description: "New line item", labor_category: "", hours: 0, direct_rate: 0, cost_type: "labor", confidence: null, source: "manual" }
        : { code, description: "New line item", qty: 1, unit: "LS", unit_price: 0, confidence: null, source: "manual" };
      return {
        ...prev,
        categories: prev.categories.map((c, i) => i !== ci ? c : ({ ...c, items: [...(c.items || []), blank] })),
      };
    });
  };

  // Delete an entire category (with confirmation). Useful when the sweep
  // added a Compliance & Contractual bucket the operator wants to price
  // manually elsewhere, or when the AI hallucinated a category.
  const removeCategory = (ci) => {
    const name = est?.categories?.[ci]?.name || "this category";
    if (!window.confirm(`Delete "${name}" and every line item inside it?`)) return;
    snapshotAndSet(
      prev => ({ ...prev, categories: prev.categories.filter((_, i) => i !== ci) }),
      `Deleted category "${name}"`,
    );
    // Purge selections for that category
    setSelected(s => {
      const next = new Set();
      for (const k of s) if (!k.startsWith(`${ci}-`)) next.add(k);
      return next;
    });
  };

  // ------------------------------------------------------------------
  // BULK ACTIONS — floating toolbar renders when any row is checked.
  const toggleRow = (ci, ii) => {
    setSelected(s => {
      const next = new Set(s);
      const k = `${ci}-${ii}`;
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = () => {
    if (selected.size === 0) return;
    const count = selected.size;
    snapshotAndSet(
      prev => ({
        ...prev,
        categories: prev.categories.map((c, ci) => ({
          ...c,
          items: (c.items || []).filter((_, ii) => !selected.has(`${ci}-${ii}`)),
        })),
      }),
      `Deleted ${count} line item${count === 1 ? "" : "s"}`,
    );
    clearSelection();
  };

  const bulkDuplicate = () => {
    if (selected.size === 0) return;
    const count = selected.size;
    snapshotAndSet(
      prev => ({
        ...prev,
        categories: prev.categories.map((c, ci) => {
          const dups = (c.items || []).flatMap((it, ii) => selected.has(`${ci}-${ii}`) ? [{
            ...it,
            code: `${it.code || ""}-COPY`,
            description: `${it.description || ""} (copy)`,
            source: "manual",
          }] : []);
          if (dups.length === 0) return c;
          return { ...c, items: [...(c.items || []), ...dups] };
        }),
      }),
      `Duplicated ${count} line item${count === 1 ? "" : "s"}`,
    );
    clearSelection();
  };

  const bulkMove = (targetCi) => {
    if (selected.size === 0) return;
    const count = selected.size;
    snapshotAndSet(
      prev => {
        // Pull selected items out
        const moving = [];
        const stripped = prev.categories.map((c, ci) => ({
          ...c,
          items: (c.items || []).filter((it, ii) => {
            if (selected.has(`${ci}-${ii}`)) { moving.push({ ...it, source: it.source || "manual" }); return false; }
            return true;
          }),
        }));
        return {
          ...prev,
          categories: stripped.map((c, ci) => ci !== targetCi ? c : ({ ...c, items: [...(c.items || []), ...moving] })),
        };
      },
      `Moved ${count} line item${count === 1 ? "" : "s"} to "${est?.categories?.[targetCi]?.name || "category"}"`,
    );
    clearSelection();
  };

  // ------------------------------------------------------------------
  // DRAG-AND-DROP within a category. dragKey holds "ci-ii" of the row
  // being dragged; drop reorders in place. Cross-category drops are
  // rejected — operators use the bulk MOVE TO menu for that so category
  // subtotals stay attributable.
  const onRowDragStart = (ci, ii) => (e) => {
    setDragKey(`${ci}-${ii}`);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs some data set on the drag or the drag doesn't start
    e.dataTransfer.setData("text/plain", `${ci}-${ii}`);
  };
  const onRowDragOver = (ci, ii) => (e) => {
    if (!dragKey) return;
    const [srcCi] = dragKey.split("-").map(Number);
    if (srcCi !== ci) { e.dataTransfer.dropEffect = "none"; return; }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onRowDrop = (ci, ii) => (e) => {
    e.preventDefault();
    if (!dragKey) return;
    const [srcCi, srcIi] = dragKey.split("-").map(Number);
    if (srcCi !== ci || srcIi === ii) { setDragKey(null); return; }
    setEst(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => {
        if (i !== ci) return c;
        const items = [...(c.items || [])];
        const [moved] = items.splice(srcIi, 1);
        // Adjust target index if we removed a row before it
        const insertAt = srcIi < ii ? ii - 1 : ii;
        items.splice(insertAt, 0, moved);
        return { ...c, items };
      }),
    }));
    setDragKey(null);
  };
  const onRowDragEnd = () => setDragKey(null);

  const save = async () => {
    if (!est) return;
    // Rescore guard: warn if the live grand total is outside the recommended band.
    if (rec?.available && grand > 0) {
      const isHigh = grand > (rec.high || 0);
      const isLow  = grand < (rec.low || 0);
      if (isHigh || isLow) {
        const direction = isHigh ? "above" : "below";
        const band = `$${rec.low?.toLocaleString(undefined, {maximumFractionDigits: 0})} – $${rec.high?.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
        const ok = window.confirm(
          `Your total ($${grand.toLocaleString(undefined,{maximumFractionDigits:0})}) is ${direction} the AI recommended band (${band}).\n\nSave anyway?`
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...est,
        mode,
        wrap_rate_id: isServices ? wrapRateId : null,
      };
      const r = await api.put(`/ai/estimate/${noticeId}`, payload);
      setEst(r.data);
      setBoe(null); // invalidate cached BOE
      const learned = r.data?._learned_count || 0;
      if (learned > 0) {
        toast.success(`Saved · Total ${r.data.grand_total ? "$" + r.data.grand_total.toLocaleString() : "—"}`, {
          description: `${learned} line item${learned === 1 ? "" : "s"} added to your workspace price book — future bids will anchor to these.`,
        });
      } else {
        toast.success(`Saved · Total ${r.data.grand_total ? "$" + r.data.grand_total.toLocaleString() : "—"}`);
      }
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  // Live grand total — services uses hours × direct × (1+wrap)(1+fee); materials uses qty × unit_price.
  const wrapMult = est?.wrap_multiplier || 0;
  const feePct = est?.fee_pct || 0;
  const grand = isServices
    ? (est?.categories || []).reduce((sum, c) => sum + (c.items || []).reduce(
        (s, it) => s + ((parseFloat(it.hours) || 0) * (parseFloat(it.direct_rate) || 0) * (1 + wrapMult) * (1 + feePct)), 0), 0)
    : (est?.categories || []).reduce((sum, c) => sum + (c.items || []).reduce(
        (s, it) => s + ((parseFloat(it.unit_price) || 0) * (parseFloat(it.qty) || 0)), 0), 0);

  // Confidence chip logic for each line item:
  //   HIGH (emerald)  — matched to uploaded past bid ≥80% OR Claude confidence ≥ 0.85
  //   MED  (amber)    — confidence 0.60-0.85 OR (LLM guess but estimate in market band)
  //   LOW  (rose)     — confidence < 0.60 OR (LLM guess AND out-of-band / no benchmark)
  const mb = est?.market_benchmark;
  const totalInBand = mb?.p25 && mb?.p75 && grand >= mb.p25 && grand <= mb.p75;
  const chipFor = (item) => {
    const src = item.price_source;
    const conf = typeof item.confidence === "number" ? item.confidence : null;
    // Historical autofill = highest confidence
    if (src?.kind === "historical_autofill" && (src.match_score ?? 0) >= 80) {
      return {
        level: "high",
        Icon: CheckCircle2,
        classes: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
        label: `Matched your past bid @ ${src.match_score}%`,
        detail: src.matched_description ? `Matched: "${src.matched_description}"` : null,
      };
    }
    // Claude self-assessed high confidence
    if (conf !== null && conf >= 0.85) {
      return {
        level: "high",
        Icon: CheckCircle2,
        classes: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
        label: `AI confidence ${(conf * 100).toFixed(0)}%`,
        detail: item.assumption ? `Basis: ${item.assumption}` : (item.source ? `Source: ${item.source}` : null),
      };
    }
    // Medium confidence OR LLM guess but total in band
    if ((conf !== null && conf >= 0.60) || (mb && totalInBand)) {
      return {
        level: "med",
        Icon: AlertTriangle,
        classes: "text-amber-300 bg-amber-400/10 border-amber-400/30",
        label: conf !== null ? `AI confidence ${(conf * 100).toFixed(0)}%` : "In-band guess",
        detail: item.assumption ? `Basis: ${item.assumption}` : (mb ? `Total in the p25-p75 market band` : null),
      };
    }
    // Low confidence — flag it
    return {
      level: "low",
      Icon: HelpCircle,
      classes: "text-rose-300 bg-rose-400/10 border-rose-400/30",
      label: conf !== null ? `AI confidence ${(conf * 100).toFixed(0)}% — verify` : "LLM guess — verify",
      detail: item.assumption ? `Basis: ${item.assumption}` : (mb ? `Total outside p25-p75 band ($${mb.p25.toLocaleString(undefined, {maximumFractionDigits:0})}-$${mb.p75.toLocaleString(undefined, {maximumFractionDigits:0})}). Double-check.` : "No market data — double-check this price."),
    };
  };

  // Compute the list of LOW-confidence rows so we can jump-navigate through them.
  const lowLines = useMemo(() => {
    const out = [];
    (est?.categories || []).forEach((cat, ci) => {
      (cat.items || []).forEach((it, ii) => {
        if (chipFor(it).level === "low") {
          out.push({ ci, ii, code: it.code, description: it.description });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [est, grand, mb, totalInBand]);

  const scrollToLow = useCallback((idx) => {
    if (!lowLines.length) return;
    const wrapped = ((idx % lowLines.length) + lowLines.length) % lowLines.length;
    setReviewIdx(wrapped);
    const target = lowLines[wrapped];
    const key = `${target.ci}-${target.ii}`;
    const row = rowRefs.current[key];
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      // Focus the unit-price input so the operator can immediately type.
      const priceInput = row.querySelector(`[data-testid="price-${target.ci}-${target.ii}"]`);
      if (priceInput) setTimeout(() => priceInput.focus(), 300);
    }
  }, [lowLines]);

  const startReview = () => {
    if (!lowLines.length) {
      toast.success("No LOW-confidence lines — every price is either from your past bids or in the market band.");
      return;
    }
    setReviewMode(true);
    setReviewIdx(0);
    scrollToLow(0);
  };
  const nextLow = () => scrollToLow(reviewIdx + 1);
  const prevLow = () => scrollToLow(reviewIdx - 1);
  const stopReview = () => setReviewMode(false);

  const suggestPrice = async (ci, ii, item) => {
    const key = `${ci}-${ii}`;
    setSuggestions((s) => ({ ...s, [key]: { loading: true, suggestion: null } }));
    try {
      const r = await api.post(`/ai/estimate/${noticeId}/suggest-price`, {
        code: item.code,
        description: item.description,
        qty: parseFloat(item.qty) || null,
        unit: item.unit,
        current_price: parseFloat(item.unit_price) || null,
      });
      setSuggestions((s) => ({ ...s, [key]: { loading: false, suggestion: r.data } }));
    } catch (err) {
      console.error("Suggest price failed:", err);
      toast.error(err?.response?.data?.detail || "Could not get suggestion");
      setSuggestions((s) => ({ ...s, [key]: { loading: false, suggestion: null } }));
    }
  };

  const acceptSuggestion = (ci, ii) => {
    const key = `${ci}-${ii}`;
    const s = suggestions[key]?.suggestion;
    if (!s) return;
    setPrice(ci, ii, String(s.unit_price));
    setSuggestions((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    toast.success(`Price updated to $${s.unit_price.toFixed(2)}`);
  };

  const dismissSuggestion = (ci, ii) => {
    const key = `${ci}-${ii}`;
    setSuggestions((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Escape closes review mode. Arrow keys navigate.
  useEffect(() => {
    if (!reviewMode) return;
    const onKey = (e) => {
      if (e.key === "Escape") stopReview();
      else if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); nextLow(); }
      else if (e.key === "ArrowUp"   || e.key === "k") { e.preventDefault(); prevLow(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode, reviewIdx, lowLines]);

  return (
    <div className="p-8 stagger-in" data-testid="estimate-page">
      <Link to={`/opportunities/${noticeId}`} className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2 mb-6" data-testid="back-to-opp"><ArrowLeft className="w-3.5 h-3.5" />BACK TO OPPORTUNITY</Link>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><Calculator className="w-3.5 h-3.5" />// ESTIMATING AGENT</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{opp?.title || "Estimate"}</h1>
          <p className="text-slate-400 mt-2">RFP scope parsed into a work-breakdown structure. Fill in your unit pricing and the totals roll up automatically.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <PageTour
            storageKey="bf_estimator_tour_v1"
            eyebrow="ESTIMATOR"
            title="Pricing this bid"
            Icon={Calculator}
            tone="fuchsia"
            steps={[
              { Icon: CalcIcon,    tone: "fuchsia", title: "1. Generate the WBS",              body: "Click GENERATE ESTIMATE to have Claude parse the RFP scope into a work-breakdown structure with categories, line items, quantities, and units. Costs 1 credit." },
              { Icon: Sparkles2,   tone: "violet",  title: "2. Fill unit prices",              body: "Type unit $ into each row. HIGH/MED/LOW chips flag confidence; the ✨ button gets an AI suggestion per line (1 credit). Extended and grand totals roll up automatically." },
              { Icon: ListChecks,  tone: "amber",   title: "3. Review LOW lines",              body: "REVIEW LOW opens a floating toolbar that jumps between every low-confidence row so you can double-check each price. Arrow keys navigate; Esc exits." },
              { Icon: CheckCircle2,tone: "emerald", title: "4. Save",                          body: "SAVE PRICING persists everything. If your total is out of the AI recommended band, BidForge will warn you before saving — nothing gets locked in blind." },
            ]}
          />
          {!est && <button onClick={() => runAgent(false)} disabled={loading} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 flex items-center gap-2 disabled:opacity-50" data-testid="generate-estimate">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}GENERATE ESTIMATE
          </button>}
          {est && <>
            <button onClick={openBoe} className="border border-violet-400/50 text-violet-300 hover:border-violet-400 hover:bg-violet-400/10 px-5 py-2.5 mono-label flex items-center gap-2" data-testid="open-boe-btn">
              <ClipboardList className="w-4 h-4" />BASIS OF ESTIMATES
            </button>
            <button onClick={() => downloadPdf(`/ai/estimate/${noticeId}/pricing-brief.pdf`, `pricing-brief.pdf`)} className="border border-fuchsia-400/50 text-fuchsia-300 hover:border-fuchsia-400 hover:bg-fuchsia-400/10 px-5 py-2.5 mono-label flex items-center gap-2" data-testid="download-brief-btn">
              <FileDown className="w-4 h-4" />PRICING BRIEF
            </button>
            {isServices && (
              <button onClick={() => downloadPdf(`/ai/estimate/${noticeId}/staffing-table.pdf`, `staffing-table.pdf`)} className="border border-fuchsia-400/50 text-fuchsia-300 hover:border-fuchsia-400 hover:bg-fuchsia-400/10 px-5 py-2.5 mono-label flex items-center gap-2" data-testid="download-staffing-btn">
                <Users2 className="w-4 h-4" />STAFFING TABLE
              </button>
            )}
            <button onClick={runServicesAgent} disabled={loading} className={`border px-5 py-2.5 mono-label flex items-center gap-2 disabled:opacity-50 transition-colors ${isServices ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10" : "border-white/20 hover:border-fuchsia-400 hover:text-fuchsia-400"}`} data-testid="services-mode-btn" title="Rebuild as a services labor-hour buildup (labor cat × hours × wrap rate)">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />}{isServices ? "SERVICES MODE" : "SWITCH TO SERVICES"}
            </button>
            <button onClick={startReview} disabled={loading || !lowLines.length} className={`border px-5 py-2.5 mono-label flex items-center gap-2 disabled:opacity-40 transition-colors ${lowLines.length ? "border-rose-400/50 text-rose-300 hover:border-rose-400 hover:bg-rose-400/10" : "border-white/10 text-slate-500"}`} data-testid="review-low-btn">
              <Zap className="w-4 h-4" />REVIEW LOW ({lowLines.length})
            </button>
            <button onClick={() => runAgent(true)} disabled={loading} className="border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-5 py-2.5 mono-label flex items-center gap-2 disabled:opacity-50" data-testid="regenerate-estimate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}REGENERATE
            </button>
            <button onClick={save} disabled={saving} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 flex items-center gap-2 disabled:opacity-50" data-testid="save-estimate">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}SAVE PRICING
            </button>
          </>}
        </div>
      </div>

      {!est && !loading && (
        <div className="border border-white/10 bg-[#0D1424] p-12 text-center" data-testid="estimate-empty">
          <Calculator className="w-10 h-10 mx-auto mb-4 text-slate-600" />
          <div className="text-slate-400 mb-2">No estimate yet.</div>
          <div className="text-slate-500 text-sm">Click <span className="text-cyan-400">GENERATE ESTIMATE</span> and the Estimating Agent will parse this solicitation into a WBS with categories, line items, quantities, and units. You'll drop in unit pricing after.</div>
        </div>
      )}

      {/* Peer wins calibration — real award totals from friends/peers this workspace
          uploaded. Verdict recomputes live against `grand` as the operator edits. */}
      <div className="mb-6">
        <PeerWinsCalibrationCard noticeId={noticeId} liveTotal={grand} />
      </div>

      {/* AI-recommended pricing baseline blending market benchmark + peer wins.
          Verdict flips as operator edits unit prices. */}
      <div className="mb-6">
        <RecommendedPricingCard noticeId={noticeId} liveTotal={grand} />
      </div>

      {est && (
        <div className="space-y-6">
          <EstimateVariantTabs
            noticeId={noticeId}
            currentEstimate={est}
            onVariantSwitch={() => {
              // After a variant switch/create/delete, refetch the primary
              // estimate — the backend has mirrored the newly-selected
              // variant into opp.estimate.
              api.get(`/ai/estimate/${noticeId}`).then((r) => setEst(r.data)).catch(() => {});
            }}
          />
          {/* Anchor pill — tells the operator which signal the estimator
              locked onto. Priority mirrors the backend: magnitude > peer > market. */}
          {est.anchor?.type && (
            <div className="rounded-full inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border border-emerald-300 text-sm" data-testid="anchor-pill">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              <span className="text-[10px] font-mono font-bold tracking-widest text-emerald-800">
                {est.anchor.type === "magnitude" && "MAGNITUDE ANCHOR"}
                {est.anchor.type === "peer" && "PEER WINS ANCHOR"}
                {est.anchor.type === "market" && "MARKET BAND ANCHOR"}
              </span>
              {est.anchor.low && est.anchor.high && (
                <span className="font-mono text-slate-700">
                  ${Number(est.anchor.low).toLocaleString(undefined, {maximumFractionDigits: 0})}–${Number(est.anchor.high).toLocaleString(undefined, {maximumFractionDigits: 0})}
                </span>
              )}
              {est.anchor.tightness_pct != null && (
                <span className="text-[10px] font-mono text-slate-500">± {est.anchor.tightness_pct}%</span>
              )}
            </div>
          )}

          {/* Magnitude warning — attachment-wins comparison. If the RFP body
              says the magnitude is $15-25k and the operator's live total is
              way over, this is a bid-killer that MUST scream before the
              proposal goes out. Only fires when we have a takeoff magnitude
              AND a live total. */}
          {opp?.attachment_takeoff?.magnitude?.low && grand > 0 && (() => {
            const magLow = Number(opp.attachment_takeoff.magnitude.low);
            const magHigh = Number(opp.attachment_takeoff.magnitude.high || magLow);
            const ratio = grand / magHigh;
            const overHigh = grand > magHigh * 1.5;
            const wayOver = ratio >= 3;
            const underLow = grand > 0 && grand < magLow * 0.5;
            if (!overHigh && !underLow) return null;
            return (
              <div className={`rounded-2xl border-2 p-5 ${wayOver ? "bg-rose-50 border-rose-500" : "bg-amber-50 border-amber-400"}`} data-testid="magnitude-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-6 h-6 shrink-0 mt-0.5 ${wayOver ? "text-rose-700" : "text-amber-700"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-display font-black text-lg ${wayOver ? "text-rose-900" : "text-amber-900"}`}>
                      {wayOver
                        ? `⚠ Your bid is ${ratio.toFixed(1)}× the published magnitude`
                        : overHigh
                        ? `Your bid is above the published magnitude`
                        : `Your bid is well below the published magnitude`}
                    </div>
                    <div className={`text-sm mt-1 ${wayOver ? "text-rose-800" : "text-amber-800"}`}>
                      RFP body states magnitude of construction is{" "}
                      <span className="font-mono font-bold">
                        ${magLow.toLocaleString()}{magHigh !== magLow ? `–$${magHigh.toLocaleString()}` : ""}
                      </span>. Your live total is{" "}
                      <span className="font-mono font-bold">${grand.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>.
                      {wayOver && " This will almost certainly get you removed from consideration — verify you're pricing the right scope before submitting."}
                    </div>
                    {opp.attachment_takeoff.magnitude.verbatim && (
                      <div className="text-[11px] font-mono text-slate-600 mt-2 italic border-l-2 border-slate-400 pl-2">
                        "{opp.attachment_takeoff.magnitude.verbatim.slice(0, 240)}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Compliance sweep is running in the background — show a subtle
              pulsing pill so the operator knows something's still coming. */}
          {sweeping && !est.compliance && (
            <div className="rounded-2xl border border-amber-300/40 bg-amber-50/50 p-4 flex items-center gap-3 text-sm text-amber-800" data-testid="compliance-sweeping">
              <Loader2 className="w-4 h-4 animate-spin" />
              <div>
                <span className="font-display font-bold">Compliance sweep in progress</span>
                <span className="text-slate-600 ml-2">Claude is scanning the RFP for bonds, insurance, wage escalation, and mandatory checklist items. This runs in the background — feel free to start editing.</span>
              </div>
            </div>
          )}

          {/* Compliance sweep — items caught by the second-pass AI that scans
              the RFP for bonds, insurance, wage escalation, cert submittal
              costs, and hard clause requirements. Priced items are already
              baked into grand_total via the new "Compliance & Contractual"
              category; this card renders the non-cost checklist that operators
              must verify BEFORE the bid goes out. */}
          {est.compliance && (est.compliance.checklist?.length > 0 || (est.compliance.priced_items_added || 0) > 0) && (
            <div className="rounded-2xl border border-amber-300/60 bg-amber-50 p-5" data-testid="compliance-card">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="mono-label text-amber-800 mb-1">// COMPLIANCE SWEEP</div>
                  <div className="font-display font-bold text-lg text-slate-900">
                    {est.compliance.priced_items_added > 0
                      ? `${est.compliance.priced_items_added} priced compliance item${est.compliance.priced_items_added === 1 ? "" : "s"} folded into your estimate`
                      : "Compliance checklist"}
                  </div>
                  {est.compliance.summary && (
                    <div className="text-sm text-slate-700 mt-1">{est.compliance.summary}</div>
                  )}
                  {est.compliance.priced_items_added > 0 && (
                    <div className="text-xs text-slate-600 mt-1">
                      Scroll to the <span className="font-mono font-bold">Compliance &amp; Contractual</span> category below to review bond, insurance, and wage-escalation line items.
                    </div>
                  )}
                  {est.compliance.checklist?.length > 0 && (
                    <div className="mt-4 grid gap-2" data-testid="compliance-checklist">
                      {est.compliance.checklist.map((c, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm" data-testid={`compliance-item-${i}`}>
                          <span
                            className={`text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded uppercase shrink-0 mt-0.5 ${
                              c.severity === "critical"
                                ? "bg-rose-100 text-rose-800 border border-rose-300"
                                : c.severity === "high"
                                ? "bg-amber-100 text-amber-800 border border-amber-300"
                                : "bg-slate-100 text-slate-700 border border-slate-300"
                            }`}
                          >
                            {c.severity}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-slate-900">{c.item}</div>
                            {c.clause_ref && <div className="text-[11px] font-mono text-slate-500">{c.clause_ref}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rate Card Auto-Match nudge — services mode only, only when suggestions exist. */}
          <RateCardAutoMatchCard noticeId={noticeId} isServices={isServices} onApplied={(newEst) => { setEst(newEst); setBoe(null); }} />

          {/* Rescore trigger — nudge when the live total is outside AI recommended band. */}
          {rec?.available && grand > 0 && !rescoreDismissed && (grand > rec.high || grand < rec.low) && (() => {
            const isHigh = grand > rec.high;
            const delta = Math.round(((grand - rec.baseline) / rec.baseline) * 100);
            return (
              <div
                className={`rounded-2xl border p-4 flex items-start gap-3 ${
                  isHigh ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-amber-50 border-amber-200 text-amber-800"
                }`}
                data-testid="rescore-nudge"
              >
                {isHigh ? <ArrowUp className="w-5 h-5 shrink-0 mt-0.5" /> : <ArrowDown className="w-5 h-5 shrink-0 mt-0.5" />}
                <div className="flex-1 text-sm leading-relaxed">
                  <div className="font-display font-bold text-base mb-1">
                    Pricing is {delta > 0 ? "+" : ""}{delta}% vs the AI baseline
                  </div>
                  <div className="text-xs">
                    Your live total ${grand.toLocaleString(undefined, {maximumFractionDigits: 0})} is {isHigh ? "above" : "below"} the recommended band (${rec.low.toLocaleString(undefined, {maximumFractionDigits: 0})} – ${rec.high.toLocaleString(undefined, {maximumFractionDigits: 0})}). {isHigh ? "Consider trimming line items or documenting why you're above market." : "Double-check you didn't miss scope."}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => runAgent(true)}
                    disabled={loading}
                    className="text-xs font-mono font-bold tracking-widest px-3 py-2 rounded-full bg-white border border-current/20 hover:brightness-95 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="rescore-regenerate"
                  >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    RESCORE
                  </button>
                  <button
                    onClick={() => setRescoreDismissed(true)}
                    className="text-xs font-mono font-bold tracking-widest px-2 py-2 rounded-full hover:bg-white/50"
                    data-testid="rescore-dismiss"
                    aria-label="Dismiss"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })()}
          {(est.categories || []).map((cat, ci) => {
            const subtotal = isServices
              ? (cat.items || []).reduce((s, it) => s + ((parseFloat(it.hours) || 0) * (parseFloat(it.direct_rate) || 0) * (1 + wrapMult) * (1 + feePct)), 0)
              : (cat.items || []).reduce((s, it) => s + ((parseFloat(it.unit_price) || 0) * (parseFloat(it.qty) || 0)), 0);
            return (
              <div key={ci} className="bg-[#0D1424] border border-white/10" data-testid={`estimate-category-${ci}`}>
                <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="mono-label text-cyan-400">{cat.name}</div>
                    {cat.source_ref && <div className="text-[11px] text-slate-500 mt-1 italic truncate">Traces to: <span className="text-slate-300">{cat.source_ref}</span></div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono text-white">Subtotal: <span className="text-cyan-400">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                    <button
                      onClick={() => removeCategory(ci)}
                      className="opacity-40 hover:opacity-100 text-rose-400 hover:text-rose-300 transition-opacity"
                      title={`Delete category "${cat.name}"`}
                      data-testid={`delete-category-${ci}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="mono-label text-slate-500">
                    <tr>
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all rows in this category"
                          checked={(cat.items || []).length > 0 && (cat.items || []).every((_, ii) => selected.has(`${ci}-${ii}`))}
                          onChange={(e) => {
                            setSelected(s => {
                              const next = new Set(s);
                              (cat.items || []).forEach((_, ii) => {
                                if (e.target.checked) next.add(`${ci}-${ii}`);
                                else next.delete(`${ci}-${ii}`);
                              });
                              return next;
                            });
                          }}
                          className="accent-cyan-400"
                          data-testid={`select-all-${ci}`}
                        />
                      </th>
                      <th className="text-left p-3 w-24">CODE</th>
                      <th className="text-left p-3">{isServices ? "LABOR CATEGORY / ROLE" : "DESCRIPTION"}</th>
                      <th className="text-right p-3 w-24">{isServices ? "HOURS" : "QTY"}</th>
                      <th className="text-left p-3 w-16">{isServices ? "TYPE" : "UNIT"}</th>
                      <th className="text-right p-3 w-32">{isServices ? "$/HR DIRECT" : "UNIT $"}</th>
                      <th className="text-right p-3 w-32">{isServices ? "COST-TO-GOV" : "EXTENDED"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cat.items || []).map((it, ii) => {
                      const hrs = parseFloat(it.hours) || 0;
                      const dr = parseFloat(it.direct_rate) || 0;
                      const ext = isServices
                        ? hrs * dr * (1 + wrapMult) * (1 + feePct)
                        : (parseFloat(it.unit_price) || 0) * (parseFloat(it.qty) || 0);
                      const chip = chipFor(it);
                      const ChipIcon = chip.Icon;
                      const isLow = chip.level === "low";
                      const isCurrent = reviewMode && lowLines[reviewIdx]?.ci === ci && lowLines[reviewIdx]?.ii === ii;
                      const dimmed = reviewMode && !isLow;
                      const valLeft  = isServices ? (it.hours ?? "")       : (it.qty ?? "");
                      const valRight = isServices ? (it.direct_rate ?? "") : (it.unit_price ?? "");
                      return (
                        <tr
                          key={ii}
                          ref={(el) => { if (el) rowRefs.current[`${ci}-${ii}`] = el; }}
                          draggable
                          onDragStart={onRowDragStart(ci, ii)}
                          onDragOver={onRowDragOver(ci, ii)}
                          onDrop={onRowDrop(ci, ii)}
                          onDragEnd={onRowDragEnd}
                          className={`border-t border-white/5 transition-all ${dimmed ? "opacity-30" : ""} ${isCurrent ? "bg-rose-400/10 ring-2 ring-rose-400/50" : ""} ${dragKey === `${ci}-${ii}` ? "opacity-50" : ""} ${selected.has(`${ci}-${ii}`) ? "bg-cyan-400/5" : ""}`}
                          data-testid={`estimate-row-${ci}-${ii}`}
                        >
                          <td className="p-3 w-10">
                            <div className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={selected.has(`${ci}-${ii}`)}
                                onChange={() => toggleRow(ci, ii)}
                                onClick={(e) => e.stopPropagation()}
                                className="accent-cyan-400"
                                data-testid={`select-row-${ci}-${ii}`}
                                aria-label="Select row"
                              />
                              <GripVertical className="w-3 h-3 text-slate-600 cursor-grab" title="Drag to reorder within this category" />
                            </div>
                          </td>
                          <td className="p-3 font-mono text-cyan-400 text-xs">
                            <div className="flex items-center gap-1.5">
                              <span
                                title={`${chip.label}${chip.detail ? " — " + chip.detail : ""}`}
                                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-mono font-bold tracking-widest ${chip.classes}`}
                                data-testid={`confidence-chip-${ci}-${ii}-${chip.level}`}
                              >
                                <ChipIcon className="w-2.5 h-2.5" />
                                {chip.level.toUpperCase()}
                              </span>
                              <span>{it.code || "-"}</span>
                            </div>
                          </td>
                          <td className="p-3 text-slate-200">
                            {isServices && it.labor_category && <div className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-300 mb-0.5">{it.labor_category}</div>}
                            <textarea
                              value={it.description || ""}
                              onChange={(e) => setDescription(ci, ii, e.target.value)}
                              rows={1}
                              className="w-full bg-transparent border border-transparent hover:border-white/10 focus:border-cyan-400 rounded px-2 py-1 text-sm text-slate-200 outline-none resize-none leading-snug"
                              style={{ minHeight: "1.75rem" }}
                              onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                              data-testid={`desc-${ci}-${ii}`}
                            />
                            {it.source_ref && (
                              <div className="text-[10px] text-slate-500 mt-0.5 italic px-2" title="Basis of Estimates — RFP paragraph this line traces to">
                                📎 {it.source_ref}
                              </div>
                            )}
                            {it.notes && <div className="text-xs text-slate-500 mt-0.5 px-2">{it.notes}</div>}
                          </td>
                          <td className="p-3"><input value={valLeft} onChange={(e) => setQty(ci, ii, e.target.value)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-2 py-1 text-sm text-right outline-none font-mono" data-testid={`qty-${ci}-${ii}`} /></td>
                          <td className="p-3 text-slate-400 uppercase text-xs">{isServices ? (it.cost_type || "labor") : it.unit}</td>
                          <td className="p-3">
                            <div className="relative flex items-center gap-1">
                              <input placeholder="$0.00" value={valRight} onChange={(e) => setPrice(ci, ii, e.target.value)} className="flex-1 bg-[#070B14] border border-white/10 focus:border-cyan-400 px-2 py-1 text-sm text-right outline-none font-mono" data-testid={`price-${ci}-${ii}`} />
                              {chip.level !== "high" && !isServices && (
                                <button
                                  onClick={() => suggestPrice(ci, ii, it)}
                                  disabled={suggestions[`${ci}-${ii}`]?.loading}
                                  className="p-1 rounded text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-400/10 disabled:opacity-50 transition-colors"
                                  title="Get an AI-suggested price for this line"
                                  data-testid={`suggest-price-${ci}-${ii}`}
                                >
                                  {suggestions[`${ci}-${ii}`]?.loading
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Sparkles className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                            {suggestions[`${ci}-${ii}`]?.suggestion && (
                              <div className="absolute z-30 mt-2 w-96 -translate-x-1/2 left-1/2 bg-[#0D1424] border-2 border-fuchsia-400/50 shadow-2xl shadow-fuchsia-500/20 rounded p-4" data-testid={`suggestion-popover-${ci}-${ii}`}>
                                <div className="flex items-start justify-between mb-2 gap-2">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-fuchsia-300" />
                                    <span className="mono-label text-fuchsia-300">AI SUGGESTION</span>
                                  </div>
                                  <span className={`text-[10px] font-mono font-bold tracking-widest rounded-full px-2 py-0.5 ${
                                    suggestions[`${ci}-${ii}`].suggestion.confidence >= 0.85 ? "text-emerald-300 bg-emerald-400/10 border border-emerald-400/30"
                                    : suggestions[`${ci}-${ii}`].suggestion.confidence >= 0.60 ? "text-amber-300 bg-amber-400/10 border border-amber-400/30"
                                    : "text-rose-300 bg-rose-400/10 border border-rose-400/30"
                                  }`}>
                                    {Math.round(suggestions[`${ci}-${ii}`].suggestion.confidence * 100)}% · {suggestions[`${ci}-${ii}`].suggestion.source}
                                  </span>
                                </div>
                                <div className="flex items-baseline gap-3 mb-2">
                                  <div className="font-display font-black text-3xl text-emerald-300">
                                    ${suggestions[`${ci}-${ii}`].suggestion.unit_price?.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-slate-400 font-mono">
                                    range ${suggestions[`${ci}-${ii}`].suggestion.unit_price_min?.toFixed(2)} — ${suggestions[`${ci}-${ii}`].suggestion.unit_price_max?.toFixed(2)}
                                  </div>
                                </div>
                                <div className="text-xs text-slate-300 leading-relaxed mb-3 max-h-32 overflow-y-auto">
                                  {suggestions[`${ci}-${ii}`].suggestion.reasoning}
                                </div>
                                <div className="flex items-center gap-2 justify-end">
                                  <button
                                    onClick={() => dismissSuggestion(ci, ii)}
                                    className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-white px-3 py-1.5"
                                    data-testid={`suggestion-dismiss-${ci}-${ii}`}
                                  >
                                    DISMISS
                                  </button>
                                  <button
                                    onClick={() => acceptSuggestion(ci, ii)}
                                    className="text-xs font-mono font-bold tracking-widest text-[#070B14] bg-fuchsia-400 hover:bg-fuchsia-300 rounded-full px-4 py-1.5 flex items-center gap-1.5"
                                    data-testid={`suggestion-accept-${ci}-${ii}`}
                                  >
                                    <Check className="w-3.5 h-3.5" />ACCEPT
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-right font-mono text-slate-200">
                            <div className="flex items-center justify-end gap-2">
                              <span>${ext.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <button
                                onClick={() => removeItem(ci, ii)}
                                className="opacity-40 hover:opacity-100 text-rose-400 hover:text-rose-300 transition-opacity"
                                title="Delete this line item"
                                data-testid={`delete-row-${ci}-${ii}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-white/5">
                      <td colSpan={7} className="p-2 text-center">
                        <button
                          onClick={() => addItem(ci)}
                          className="text-[10px] font-mono font-bold tracking-widest text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10 rounded-full px-4 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                          data-testid={`add-row-${ci}`}
                        >
                          <Plus className="w-3 h-3" />ADD LINE ITEM
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}

          <div className="bg-cyan-400/10 border border-cyan-400 p-5 flex items-center justify-between" data-testid="estimate-total">
            <div className="mono-label text-cyan-400">GRAND TOTAL</div>
            <div className="font-display font-black text-3xl">${grand.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>

          {/* Floating bulk toolbar — visible whenever any row is checked. Sticks
              to the bottom of the viewport so operators can review changes above
              before they act. */}
          {selected.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0D1424] border-2 border-cyan-400 shadow-2xl shadow-cyan-500/30 rounded-full px-4 py-2.5 flex items-center gap-3" data-testid="bulk-toolbar">
              <span className="mono-label text-cyan-400 pl-2">{selected.size} SELECTED</span>
              <span className="text-slate-600">·</span>
              <button
                onClick={bulkDelete}
                className="text-xs font-mono font-bold tracking-widest text-rose-300 hover:text-rose-200 hover:bg-rose-400/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="bulk-delete"
              >
                <Trash2 className="w-3.5 h-3.5" />DELETE
              </button>
              <button
                onClick={bulkDuplicate}
                className="text-xs font-mono font-bold tracking-widest text-emerald-300 hover:text-emerald-200 hover:bg-emerald-400/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="bulk-duplicate"
              >
                <Copy className="w-3.5 h-3.5" />DUPLICATE
              </button>
              <div className="relative group">
                <button
                  className="text-xs font-mono font-bold tracking-widest text-violet-300 hover:text-violet-200 hover:bg-violet-400/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="bulk-move-btn"
                >
                  <MoveRight className="w-3.5 h-3.5" />MOVE TO...
                </button>
                <div className="absolute bottom-full mb-2 right-0 hidden group-hover:block group-focus-within:block bg-[#0D1424] border border-violet-400/40 rounded shadow-xl shadow-black/50 min-w-[240px] max-h-72 overflow-y-auto z-50">
                  {(est.categories || []).map((c, ci) => (
                    <button
                      key={ci}
                      onClick={() => bulkMove(ci)}
                      className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-violet-400/10 hover:text-white transition-colors font-mono"
                      data-testid={`bulk-move-to-${ci}`}
                    >
                      → {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <span className="text-slate-600">·</span>
              <button
                onClick={clearSelection}
                className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-white hover:bg-white/5 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="bulk-clear"
              >
                <X className="w-3.5 h-3.5" />CLEAR
              </button>
            </div>
          )}

          {est.assumptions?.length > 0 && (
            <div className="bg-[#0D1424] border border-white/10 p-5">
              <div className="mono-label text-amber-400 mb-3">ASSUMPTIONS</div>
              <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">{est.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </div>
          )}
          {est.exclusions?.length > 0 && (
            <div className="bg-[#0D1424] border border-white/10 p-5">
              <div className="mono-label text-slate-400 mb-3">EXCLUSIONS</div>
              <ul className="list-disc list-inside space-y-1 text-slate-300 text-sm">{est.exclusions.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </div>
          )}

          {/* Services-mode summary strip: hours, wrap, fee, cost-to-gov, price. */}
          {isServices && (
            <div className="bg-gradient-to-br from-fuchsia-950/40 via-[#0D1424] to-violet-950/40 border border-fuchsia-500/40 p-5" data-testid="services-summary">
              <div className="mono-label text-fuchsia-300 mb-3">// SERVICES BUILDUP · FAR COST STRUCTURE</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400">TOTAL HOURS</div>
                  <div className="font-display font-black text-xl text-white font-mono">{(est.total_hours || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400">WRAP MULT</div>
                  <div className="font-display font-black text-xl text-violet-300 font-mono">{(wrapMult * 100).toFixed(1)}%</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400">COST TO GOV</div>
                  <div className="font-display font-black text-xl text-amber-300 font-mono">${(est.grand_cost_to_gov || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <div className="text-[9px] font-mono font-bold tracking-widest text-slate-400">PRICE (w/ FEE {(feePct * 100).toFixed(1)}%)</div>
                  <div className="font-display font-black text-xl text-emerald-300 font-mono">${(est.grand_total || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                </div>
              </div>

              {/* Applied rate-card snaps — each is revertable in one click. */}
              {(est.rate_card_snaps || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10" data-testid="snap-chips">
                  <div className="text-[9px] font-mono font-bold tracking-widest text-fuchsia-300 mb-2">
                    APPLIED RATE-CARD SNAPS ({est.rate_card_snaps.length}) — CLICK ✕ TO REVERT
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {est.rate_card_snaps.map((s, i) => {
                      const key = `${s.ci}-${s.ii}`;
                      const rateDelta = (s.to_rate || 0) - (s.from_rate || 0);
                      const isLocked = s.reason === "rate_locked";
                      return (
                        <div
                          key={i}
                          className={`inline-flex items-center gap-2 rounded-full pl-3 pr-1 py-1 text-xs border transition-colors ${
                            isLocked
                              ? "bg-amber-500/10 border-amber-400/40 text-amber-200"
                              : "bg-emerald-500/10 border-emerald-400/40 text-emerald-200"
                          }`}
                          data-testid={`snap-chip-${key}`}
                        >
                          <span className="font-mono">
                            {s.from_label} <span className="opacity-60">→</span> <b>{s.to_label}</b>{" "}
                            <span className="opacity-70">(${s.from_rate?.toFixed(2)} → ${s.to_rate?.toFixed(2)}
                            {rateDelta ? <span className={rateDelta > 0 ? "text-emerald-300" : "text-rose-300"}>{" "}
                              {rateDelta > 0 ? "+" : ""}{rateDelta.toFixed(2)}</span> : null}
                            )</span>
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                const r = await api.post(`/ai/estimate/${noticeId}/revert-snap`, { category_index: s.ci, item_index: s.ii });
                                setEst(r.data.estimate);
                                setBoe(null);
                                toast.success(`Reverted to "${r.data.reverted_to}" @ $${r.data.new_rate?.toFixed(2)}/hr`);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Revert failed");
                              }
                            }}
                            className="w-5 h-5 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                            aria-label="Revert this snap"
                            data-testid={`snap-revert-${key}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Basis of Estimates side drawer — every line paired with its RFP source_ref. */}
      {boeOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="boe-drawer">
          <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={() => setBoeOpen(false)} />
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">// BASIS OF ESTIMATES</div>
                <div className="font-display font-bold text-lg text-slate-900">Traceable cost build</div>
              </div>
              <button onClick={() => setBoeOpen(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid="boe-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {!boe ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
              ) : (
                <>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-4 flex items-start gap-3" data-testid="boe-coverage">
                    <div className="flex-1 text-xs text-emerald-800">
                      <b>{boe.lines_with_refs?.length || 0}</b> of <b>{boe.total_lines}</b> lines have RFP source references. {boe.lines_missing_refs?.length ? `${boe.lines_missing_refs.length} still need attribution — auto-fill or click each line to attach one.` : "Every line traces back — audit-ready."}
                    </div>
                    {boe.lines_missing_refs?.length > 0 && (
                      <button
                        onClick={bulkFillBoe}
                        disabled={bulkFilling}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono font-bold tracking-widest text-white bg-gradient-to-br from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-50"
                        data-testid="boe-bulk-fill"
                      >
                        {bulkFilling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        AUTO-FILL ALL
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {(boe.lines_with_refs || []).concat(boe.lines_missing_refs || []).map((l, i) => (
                      <div key={i} className={`rounded-xl border p-3 ${l.source_ref ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"}`} data-testid={`boe-line-${i}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">{l.category}{l.code && <> · {l.code}</>}</div>
                            <div className="text-sm text-slate-900 mt-0.5">{l.description}</div>
                            {l.source_ref ? (
                              <div className="text-xs text-violet-700 mt-1">📎 {l.source_ref}</div>
                            ) : (
                              <button
                                onClick={() => openPicker(l)}
                                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold tracking-widest text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 transition-colors"
                                data-testid={`boe-fix-${i}`}
                              >
                                <Sparkles className="w-3 h-3" />ATTACH RFP REFERENCE
                              </button>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {l.hours ? <div className="text-xs font-mono text-slate-600">{l.hours.toLocaleString()} hrs</div> : null}
                            {l.direct_rate ? <div className="text-xs font-mono text-slate-500">@ ${l.direct_rate}/hr</div> : null}
                            {l.qty ? <div className="text-xs font-mono text-slate-600">{l.qty} {l.unit}</div> : null}
                            {l.unit_price ? <div className="text-xs font-mono text-slate-500">@ ${l.unit_price}</div> : null}
                            <div className="text-sm font-mono font-bold text-slate-900">${(l.cost_to_gov || l.extended_price || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                          </div>
                        </div>
                        {l.assumption && <div className="text-[11px] text-slate-500 italic mt-2">Basis: {l.assumption}</div>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BOE Auto-Fill picker — user searches RFP paragraphs and clicks one to attach. */}
      {boePicker && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="boe-picker-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">ATTACH RFP REFERENCE</div>
                <div className="font-display font-bold text-lg text-slate-900 truncate">{boePicker.description}</div>
                <div className="text-xs text-slate-500 mt-0.5">Pick the paragraph that best supports this line. The first 140 chars become the source_ref.</div>
              </div>
              <button onClick={() => setBoePicker(null)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600 shrink-0" data-testid="boe-picker-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {boePicker.loading && (
                <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Ranking RFP paragraphs…</div>
              )}
              {!boePicker.loading && (boePicker.candidates || []).length === 0 && (
                <div className="text-sm text-slate-500 p-8 text-center border border-dashed border-slate-300 rounded-xl" data-testid="boe-picker-empty">
                  No paragraphs found in the RFP description. This opportunity may not have a long-form scope pasted — try attaching a reference manually by editing the line.
                </div>
              )}
              {(boePicker.candidates || []).map((c, i) => {
                const strength = c.score >= 0.5 ? "strong" : c.score >= 0.25 ? "medium" : "weak";
                const strengthClasses = {
                  strong: "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800",
                  medium: "border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800",
                  weak:   "border-slate-200 bg-white hover:bg-slate-50 text-slate-700",
                }[strength];
                return (
                  <button
                    key={c.index}
                    onClick={() => attachRef(c.excerpt)}
                    className={`w-full text-left rounded-xl border p-3 transition-colors ${strengthClasses}`}
                    data-testid={`boe-candidate-${i}`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <div className="text-[10px] font-mono font-bold tracking-widest">
                        MATCH · {(c.score * 100).toFixed(0)}% overlap · {c.overlap} shared term{c.overlap === 1 ? "" : "s"}
                      </div>
                      <div className="text-[10px] font-mono font-bold tracking-widest">¶ #{c.index + 1}</div>
                    </div>
                    <div className="text-sm leading-relaxed">{c.excerpt}</div>
                  </button>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-500">
              Don't see a good match? Close and edit the line's <code>source_ref</code> field directly to enter any citation you want.
            </div>
          </div>
        </div>
      )}

      {/* Floating review-mode toolbar: appears while reviewing LOW-confidence lines,
          jumps between them, exits on Escape or the ✕ button. */}
      {reviewMode && lowLines.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#0D1424] border-2 border-rose-400/50 shadow-2xl shadow-rose-500/20 rounded-full pl-5 pr-2 py-2 backdrop-blur"
          data-testid="review-toolbar"
        >
          <div className="text-xs font-mono font-bold tracking-widest text-rose-300 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" />
            REVIEWING <span className="text-white text-sm">{reviewIdx + 1}</span>
            <span className="text-slate-500">/</span>
            <span className="text-slate-300">{lowLines.length}</span>
          </div>
          <div className="text-xs text-slate-400 max-w-[280px] truncate hidden md:block">
            <span className="font-mono text-cyan-400">{lowLines[reviewIdx]?.code || "-"}</span>
            <span className="mx-2 text-slate-600">·</span>
            {lowLines[reviewIdx]?.description}
          </div>
          <div className="flex items-center gap-1 ml-2">
            <button onClick={prevLow} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition-colors" data-testid="review-prev">
              <ChevronUp className="w-4 h-4" />
            </button>
            <button onClick={nextLow} className="w-8 h-8 rounded-full bg-rose-400 hover:bg-rose-300 flex items-center justify-center text-[#070B14] transition-colors" data-testid="review-next">
              <ChevronDown className="w-4 h-4" />
            </button>
            <button onClick={stopReview} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors ml-1" data-testid="review-close" title="Exit review (Esc)">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
